// src/utils/agentIdentityBridge.js
// Guarantees that while a partner is signed in, EVERY request to RouteChef's own APIs
// (fetch, XHR/axios, WebSocket) carries the partner identity, even from code that still
// builds ids with getAnonHash() or localStorage "routechef_user_id".
//
// What it does, only when a partner session exists and only for RouteChef API hosts:
//   From / from      "webbot_new:anonab12" -> "webbot_agent:ramesh.kulkarni32@gmail.com"
//   user_id / userId "webbot_trip:x1y2"    -> "webbot_agent:ramesh.kulkarni32@gmail.com"
//                    "anonab12"            -> "ramesh.kulkarni32@gmail.com"
//   + adds agent_token so the backend can prove the partner is really signed in.
// Non-web ids (e.g. "whatsapp:+91...") and non-JSON bodies are never touched.
// With no partner signed in, requests pass through unchanged.
//
// Imported once for its side effects (Navbar.js does it). Long term, callers should use
// getFromField() from identity.js; this stays as a safety net.

import { getAgentSession, verifyAgentSession } from "./agentAuth";

const API_HOSTS = [/\.execute-api\.ap-south-1\.amazonaws\.com$/i, /(^|\.)routechef\.com$/i];
const ID_KEYS = ["From", "from", "user_id", "userId", "mobnum", "old_mobnum"];
const WEB_ID_RE = /^(webbot[\w-]*:.*|anon[0-9a-f]+)$/i;
const DEBUG = process.env.NODE_ENV !== "production";
const log = (...a) => DEBUG && console.debug("[agent-bridge]", ...a);

function isOurApi(url) {
  try {
    const u = new URL(url, window.location.href);
    if (u.hostname === window.location.hostname) return true; // same origin + setupProxy in dev
    return API_HOSTS.some((re) => re.test(u.hostname));
  } catch {
    return false;
  }
}

// Rewrites id fields on one plain object (and its nested `body` object, used by the chat
// WebSocket: { headers, body: { From, ... } }). Returns true if anything was an id field.
function stampObject(obj, s) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  const email = s.profile.email;
  let touched = false;
  for (const k of ID_KEYS) {
    const v = obj[k];
    if (typeof v !== "string" || !WEB_ID_RE.test(v)) continue;
    obj[k] = v.includes(":") ? `webbot_agent:${email}` : email;
    touched = true;
  }
  if (stampObject(obj.body, s)) touched = true;
  if (touched) {
    obj.agent_token = s.token;
    // chat WebSocket carries auth in headers.Authorization
    if (obj.headers && typeof obj.headers === "object" && !Array.isArray(obj.headers)) {
      obj.headers.Authorization = s.token;
    }
  }
  return touched;
}

export function stampAgentIdentity(body) {
  const s = getAgentSession();
  if (!s) return body;

  // form-encoded (URLSearchParams object or "a=1&b=2" string), e.g. chat history fetcher
  const isParams = typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams;
  if (isParams || (typeof body === "string" && /^[\w%.-]+=/.test(body) && !body.trim().startsWith("{"))) {
    const params = new URLSearchParams(isParams ? body.toString() : body);
    let touched = false;
    for (const k of ID_KEYS) {
      const v = params.get(k);
      if (v && WEB_ID_RE.test(v)) {
        params.set(k, v.includes(":") ? `webbot_agent:${s.profile.email}` : s.profile.email);
        touched = true;
      }
    }
    if (!touched) return body;
    params.set("authorization", s.token);
    log("stamped (form)", params.get("mobnum") || params.get("From"));
    return isParams ? params : params.toString();
  }

  if (typeof body !== "string" || body.length > 2_000_000) return body;
  let obj;
  try {
    obj = JSON.parse(body);
  } catch {
    return body;
  }
  if (!stampObject(obj, s)) return body;
  log("stamped", obj.From || obj.body?.From || obj.user_id || obj.mobnum);
  return JSON.stringify(obj);
}

function install() {
  if (typeof window === "undefined" || window.__rcAgentBridge) return;
  window.__rcAgentBridge = true;

  // fetch: fetch(url, { body }) and fetch(new Request(url, { body }))
  if (window.fetch) {
    const origFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      try {
        const isReq = typeof Request !== "undefined" && input instanceof Request;
        const url = typeof input === "string" ? input : input?.href || input?.url;
        if (getAgentSession() && isOurApi(url)) {
          if (init?.body != null) {
            init = { ...init, body: stampAgentIdentity(init.body) };
          } else if (isReq && !["GET", "HEAD"].includes(input.method)) {
            const text = await input.clone().text();
            const stamped = stampAgentIdentity(text);
            if (stamped !== text) input = new Request(input, { body: stamped });
          }
        }
      } catch (e) {
        log("fetch stamp skipped", e);
      }
      return origFetch(input, init);
    };
  }

  // navigator.sendBeacon
  if (navigator.sendBeacon) {
    const origBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = (url, data) => {
      try {
        if (typeof data === "string" && isOurApi(url)) data = stampAgentIdentity(data);
      } catch {
        /* ignore */
      }
      return origBeacon(url, data);
    };
  }

  // XMLHttpRequest (axios uses this)
  if (window.XMLHttpRequest) {
    const XHR = window.XMLHttpRequest.prototype;
    const origOpen = XHR.open;
    const origSend = XHR.send;
    XHR.open = function (method, url, ...rest) {
      this.__rcUrl = url;
      return origOpen.call(this, method, url, ...rest);
    };
    XHR.send = function (body) {
      try {
        if (isOurApi(this.__rcUrl)) body = stampAgentIdentity(body);
      } catch {
        /* ignore */
      }
      return origSend.call(this, body);
    };
  }

  // WebSocket (useTravelWebSocket etc.)
  if (window.WebSocket) {
    const WSP = window.WebSocket.prototype;
    const origWsSend = WSP.send;
    WSP.send = function (data) {
      try {
        if (isOurApi(this.url.replace(/^ws/, "http"))) data = stampAgentIdentity(data);
      } catch {
        /* ignore */
      }
      return origWsSend.call(this, data);
    };
  }

  log("installed", getAgentSession() ? `partner=${getAgentSession().profile.email}` : "no partner session");

  // Re-verify the partner session on each visit and when the tab comes back into focus
  verifyAgentSession({ force: true });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") verifyAgentSession();
  });
}

install();
